import React, { useEffect, useRef, useState } from "react";

const LisVocabularyTable = ({
  t,
  vocabularyData,
  columns,
  pkField,
  booleanFields = [],
  onFieldChange,
  onSaveRow,
  onDeleteRow,
  onAddRow,
  vocabularies,
  selectedVocabulary,
  isEditing,
  onToggleEdit,
  onSaveChanges,
  onCancelEdit,
  hasUnsavedChanges,
  uploadProgress,
}) => {
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(550);

  // Format a date/time value for a datetime-local input using local time (no timezone shift)
  const toLocalDateTimeInputValue = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    const timezoneOffsetMinutes = date.getTimezoneOffset();
    const localTime = new Date(date.getTime() - timezoneOffsetMinutes * 60000);
    return localTime.toISOString().slice(0, 16);
  };

  // Dynamically size the table container to nearly full viewport height
  useEffect(() => {
    const recalc = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // Prefer visualViewport height for mobile correctness (address bar, chrome)
      const viewportHeight =
        (window.visualViewport && window.visualViewport.height) ||
        window.innerHeight;
      // Use a slightly larger bottom margin on very small screens
      const bottomMargin = viewportHeight < 700 ? 48 : 24;
      // Available space from the container's top to (almost) bottom of viewport
      const available = viewportHeight - rect.top - bottomMargin;
      // Never exceed the available space; cap to a sane max to avoid giant tables on desktops
      const maxCap = 1200;
      const height = Math.max(0, Math.min(available, maxCap));
      setContainerHeight(height);
    };
    recalc();
    window.addEventListener("resize", recalc);
    window.addEventListener("orientationchange", recalc);
    window.addEventListener("load", recalc);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", recalc);
    }
    // Recalc after paint to account for layout shifts (e.g., progress bar)
    const tId = setTimeout(recalc, 0);
    // Also schedule a frame-based and delayed recalc to catch async layout shifts (menus, breadcrumbs, fonts)
    const rafId = window.requestAnimationFrame(recalc);
    const tId2 = setTimeout(recalc, 200);

    // Observe parent size changes to react to header/breadcrumb/menu injections
    let resizeObserver;
    try {
      if (window.ResizeObserver && containerRef.current) {
        const target =
          containerRef.current.parentElement || containerRef.current;
        resizeObserver = new ResizeObserver(() => recalc());
        resizeObserver.observe(target);
      }
    } catch (e) {
      // no-op if ResizeObserver not available
    }

    return () => {
      clearTimeout(tId);
      clearTimeout(tId2);
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", recalc);
      window.removeEventListener("orientationchange", recalc);
      window.removeEventListener("load", recalc);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", recalc);
      }
      if (resizeObserver) {
        try {
          resizeObserver.disconnect();
        } catch (e) {
          // ignore
        }
      }
    };
    // Re-run when elements that affect vertical space change
  }, [uploadProgress, isEditing, selectedVocabulary, vocabularyData.length]);
  const color = isEditing ? "primary" : "success";
  const selectedVocabularyLabel = vocabularies?.[selectedVocabulary]
    ?.displayNameKey
    ? t(vocabularies[selectedVocabulary].displayNameKey, { ns: "lisLabels" })
    : selectedVocabulary || "";

  return (
    <>
      <div
        className={`d-flex align-items-center justify-content-between mt-2 w-100 rounded-top-3 border border-bottom-0 border-dark-subtle bg-body-secondary ${
          isEditing ? "" : " "
        }`}
        style={{ borderLeft: `6px solid var(--bs-${color}) !important` }}
      >
        <div className="px-3 fw-semibold text-black d-flex align-items-center">
          {isEditing ? (
            <p className="text-primary mb-0 me-2">{t("editing", { ns: "lisLabels" })} :</p>
          ) : (
            <p className="text-success mb-0 me-2">{t("viewing", { ns: "lisLabels" })} :</p>
          )}
          <div className="text-black">{selectedVocabularyLabel}</div>
          <span className="badge bg-secondary ms-2">{vocabularyData.length} elementi</span>
        </div>
        <div className="d-flex gap-2 btn-group  p-2 pe-3">
          {isEditing && (
            <button
              className="btn py-2 rounded-2  btn-primary"
              onClick={() => onAddRow()}
            >
              {" "}
              + {t("addRow", { ns: "lisLabels" })}
            </button>
          )}
          {!isEditing ? (
            <button
              className={`btn py-2 rounded-2  btn-success `}
              onClick={onToggleEdit}
            >
              {t("edit", { ns: "lisLabels" })}
            </button>
          ) : (
            <>
              <button
                className={` btn py-2 rounded-2  btn-success`}
                onClick={onSaveChanges}
                disabled={!hasUnsavedChanges}
                title={hasUnsavedChanges ? t("saveAllChanges", { ns: "lisLabels" }) : t("noChangesToSave", { ns: "lisLabels" })}
              >
                <i className="fas fa-save me-2"></i>
                {t("saveChanges", { ns: "lisLabels" })}
              </button>
              <button
                className={` btn py-2 rounded-2  btn-secondary`}
                onClick={onCancelEdit}
                title={t("cancelEditing", { ns: "lisLabels" })}
              >
                <i className="fas fa-times me-2"></i>
                {t("cancel", { ns: "lisLabels" })}
              </button>
            </>
          )}
        </div>
      </div>
      {typeof uploadProgress === "number" && uploadProgress > 0 && (
        <div className="bg-body-secondary border-start border-end px-3 pt-2 pb-1">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="text-muted">{t("importProgress", { ns: "lisLabels" })}</small>
            <small className="text-muted">{uploadProgress}%</small>
          </div>
          <div className="progress" style={{ height: "8px" }}>
            <div
              className={`progress-bar ${uploadProgress < 100 ? "progress-bar-striped progress-bar-animated" : "bg-success"}`}
              role="progressbar"
              style={{ width: `${uploadProgress}%` }}
              aria-valuenow={uploadProgress}
              aria-valuemin="0"
              aria-valuemax="100"
            />
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className=" rounded-bottom-3 bg-body-secondary  "
        style={{
          height: `${containerHeight}px`,
          maxHeight: `${containerHeight}px`,
          overflowY: "auto",
          borderLeft: `6px solid var(--bs-dark)`,
          borderCollapse: "separate",
          borderSpacing: 0,
          borderRight: "1px solid var(--bs-border-color)",
          borderBottom: "1px solid var(--bs-border-color)",
        }}
      >
        <table
          className={`table table-sm lis-vocab-table ${
            isEditing ? "table-active" : ""
          }`}
          style={{}}
        >
          <thead
            className="sticky-top bg-body"
            style={{
              top: 0,
              zIndex: 1,
              boxShadow: "0 1px 0 0 var(--bs-border-color)",
            }}
          >
            <tr>
              <th>{t("validFrom", { ns: "lisLabels" })}</th>
              <th>{t("validTo", { ns: "lisLabels" })}</th>
              {columns.map((col, colIdx) => (
                <th key={colIdx}>{t(col, { ns: "lisLabels" })}</th>
              ))}
              <th>{t("source", { ns: "lisLabels" })}</th>
              {isEditing && <th>{t("actions", { ns: "lisLabels" })}</th>}
            </tr>
          </thead>
          <tbody className="">
            {vocabularyData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 4}
                  className="text-center text-muted"
                >
                  {t("noDataAvailable", { ns: "lisLabels" })}
                </td>
              </tr>
            ) : (
              vocabularyData.map((row, index) => {
                const isActive = !row.ValidTo;
                const rowClass = isActive ? "" : "table-secondary";

                return (
                  <tr key={row._rowId || index} className={rowClass}>
                    <td>
                      <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={toLocalDateTimeInputValue(row.ValidFrom)}
                        onChange={(e) =>
                          onFieldChange(index, "ValidFrom", e.target.value)
                        }
                        disabled={!isEditing}
                      />
                    </td>
                    <td>
                      <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        disabled={!isEditing}
                        value={toLocalDateTimeInputValue(row.ValidTo)}
                        placeholder={t("activeNull", { ns: "lisLabels" })}
                        onChange={(e) =>
                          onFieldChange(index, "ValidTo", e.target.value)
                        }
                      />
                    </td>
                    
                    {columns.map((col, colIdx) => {
                      // Primary key fields should be editable only for new rows
                      const isPrimaryKey = col === pkField;
                      const isPkReadonly = isPrimaryKey && !row.isNew;
                      const isDisabled = !isEditing || isPkReadonly;
                      
                      // Check if this is a boolean field
                      const isBooleanField = booleanFields.includes(col);
                      
                      if (isBooleanField) {
                        // Render checkbox for boolean fields
                        // Convert value to boolean (handle 1/0, true/false, "true"/"false", etc.)
                        const boolValue = row[col] === 1 || row[col] === true || row[col] === "1" || row[col] === "true";
                        
                        return (
                          <td key={colIdx} className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={boolValue}
                              disabled={isDisabled}
                              onChange={(e) =>
                                onFieldChange(index, col, e.target.checked ? 1 : 0)
                              }
                              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                            />
                          </td>
                        );
                      }
                      
                      return (
                        <td key={colIdx}>
                          <input
                            type="text"
                            className={`form-control form-control-sm ${isPkReadonly ? 'bg-light' : ''}`}
                            value={row[col] || ""}
                            disabled={isDisabled}
                            onChange={(e) =>
                              onFieldChange(index, col, e.target.value)
                            }
                            title={isPkReadonly ? t("primaryKeyReadonly", { ns: "lisLabels" }) : ""}
                          />
                        </td>
                      );
                    })}
                    <td>
                      <select
                        className="form-select py-2 border "
                        value={row.Source || "LOCAL"}
                        disabled={!isEditing}
                        onChange={(e) =>
                          onFieldChange(index, "Source", e.target.value)
                        }
                      >
                        <option value="LOCAL">LOCAL</option>
                        <option value="NOMOS">NOMOS</option>
                        <option value="WHO">WHO</option>
                      </select>
                    </td>
                    {isEditing && (
                      <td>
                        {row.isDirty || row.isNew ? (
                          <button
                            className="btn btn-sm btn-success me-1"
                            // onClick={() => onSaveRow(index)}
                            // disabled={!row.isDirty && !row.isNew}
                            title={t("save", { ns: "lisLabels" })}
                          >
                            <i className="fas fa-save"></i>
                          </button>
                        ) : (
                          <></>
                        )}

                        <button
                          className="btn btn-sm btn-danger me-1"
                          onClick={() => onDeleteRow(index)}
                          title={
                            row.isNew
                              ? t("delete", { ns: "lisLabels" })
                              : t("confirmDeleteMessage", { ns: "lisLabels" })
                          }
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default LisVocabularyTable;
